import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Card } from '../UI';
import { reportsAPI } from '../../services/api';
import {
  HiOutlineUsers,
  HiOutlinePhone,
  HiOutlineClipboardCheck,
  HiOutlinePhoneIncoming,
  HiOutlinePhoneMissedCall,
  HiOutlineSwitchHorizontal,
  HiOutlineCalendar,
} from 'react-icons/hi';

/**
 * TodayOverviewKPIGrid
 * "Today's Overview" — six KPI cards in a two-column grid showing the
 * performance of ALL users for the selected day:
 *   Total Leads • Calls Made • Follow-ups • Connected • Not Connected • Conversions
 *
 * - Date picker in the header lets the grid show ANY day's overview
 *   (defaults to today; future dates are blocked)
 * - Every card is clickable and jumps to the related page
 *   (Leads / Call Logs / Follow-ups) filtered where the target page
 *   supports deep-linking
 */

const KPI_STYLES = {
  indigo:  { chip: 'from-indigo-500 to-indigo-600 ring-indigo-500/20',     bar: 'from-indigo-500 to-indigo-300',   glow: 'bg-indigo-500',   valueText: 'text-indigo-700' },
  blue:    { chip: 'from-blue-500 to-blue-600 ring-blue-500/20',           bar: 'from-blue-500 to-blue-300',       glow: 'bg-blue-500',     valueText: 'text-blue-700' },
  purple:  { chip: 'from-purple-500 to-purple-600 ring-purple-500/20',     bar: 'from-purple-500 to-purple-300',   glow: 'bg-purple-500',   valueText: 'text-purple-700' },
  green:   { chip: 'from-green-500 to-green-600 ring-green-500/20',        bar: 'from-green-500 to-green-300',     glow: 'bg-green-500',    valueText: 'text-green-700' },
  red:     { chip: 'from-red-500 to-red-600 ring-red-500/20',              bar: 'from-red-500 to-red-300',         glow: 'bg-red-500',      valueText: 'text-red-700' },
  amber:   { chip: 'from-amber-500 to-amber-600 ring-amber-500/20',        bar: 'from-amber-500 to-amber-300',     glow: 'bg-amber-500',    valueText: 'text-amber-700' },
};

const TodayOverviewKPIGrid = () => {
  const navigate = useNavigate();
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const isToday = selectedDate === todayStr;

  // Org-wide daily work report for the selected date
  const { data: reportData, isLoading } = useQuery({
    queryKey: ['kpiOverview', selectedDate],
    queryFn: async () => {
      try {
        const response = await reportsAPI.getDailyWorkReport({ date: selectedDate });
        return response.data?.data || {};
      } catch (error) {
        return {};
      }
    },
  });

  const summary = reportData?.summary || {};
  const isLoadingOrEmpty = isLoading || !reportData;

  const callsMade = summary.totalCalls || 0;
  const connected = summary.totalConnected || 0;
  const notConnected = summary.totalNotConnected || 0;
  const conversions = summary.totalConverted || 0;
  const leads = summary.totalLeadsWorked || 0;

  // Only users who checked in (have an attendance record) count as working
  const workingUsers = summary.present || 0;

  const connectionRate = callsMade > 0 ? ((connected / callsMade) * 100).toFixed(1) : '0.0';
  const conversionRate = leads > 0 ? ((conversions / leads) * 100).toFixed(1) : '0.0';

  // Where each card navigates (target pages support these URL params)
  const followUpsLink = isToday ? '/app/follow-ups?dateRange=today' : '/app/follow-ups';
  const kpis = [
    {
      title: 'Total Leads',
      value: leads,
      icon: HiOutlineUsers,
      color: 'indigo',
      subtitle: `${workingUsers} ${workingUsers === 1 ? 'user' : 'users'} working ${isToday ? 'today' : 'that day'}`,
      to: '/app/leads',
    },
    {
      title: 'Calls Made',
      value: callsMade,
      icon: HiOutlinePhone,
      color: 'blue',
      subtitle: `${workingUsers} of ${summary.totalUsers || 0} present ${isToday ? 'today' : 'that day'}`,
      to: '/app/call-logs',
    },
    {
      title: 'Follow-ups',
      value: summary.totalFollowUps || 0,
      icon: HiOutlineClipboardCheck,
      color: 'purple',
      subtitle: isToday ? 'due today' : 'for the day',
      to: followUpsLink,
    },
    {
      title: 'Connected',
      value: connected,
      icon: HiOutlinePhoneIncoming,
      color: 'green',
      subtitle: `${connectionRate}% connection rate`,
      to: '/app/call-logs',
    },
    {
      title: 'Not Connected',
      value: notConnected,
      icon: HiOutlinePhoneMissedCall,
      color: 'red',
      subtitle: callsMade > 0 ? `${((notConnected / callsMade) * 100).toFixed(1)}% of calls` : '—',
      to: '/app/call-logs',
    },
    {
      title: 'Conversions',
      value: conversions,
      icon: HiOutlineSwitchHorizontal,
      color: 'amber',
      subtitle: `${conversionRate}% conversion rate`,
      to: '/app/leads?stage=converted',
    },
  ];

  return (
    <div className="space-y-4">
      {/* Section Header + Date Picker */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md ring-2 bg-gradient-to-br from-indigo-500 to-indigo-600 ring-indigo-500/20">
            <span className="text-white"><HiOutlineUsers className="w-4 h-4" /></span>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {isToday ? "Today's Overview" : `Overview — ${format(new Date(selectedDate), 'MMM d, yyyy')}`}
            </h2>
            <p className="text-sm text-gray-500">
              Performance of all users{' '}
              <span className="font-medium text-gray-700">
                {isToday ? 'today' : 'that day'}
              </span>
            </p>
          </div>
        </div>
        {/* Date picker — any day up to today */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <HiOutlineCalendar className="w-4 h-4 text-gray-400" />
          <input
            type="date"
            max={todayStr}
            value={selectedDate}
            onChange={(e) => e.target.value && setSelectedDate(e.target.value)}
            className="px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          {!isToday && (
            <button
              type="button"
              onClick={() => setSelectedDate(todayStr)}
              className="px-2.5 py-1.5 rounded-lg text-xs font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 transition-colors"
            >
              Today
            </button>
          )}
        </div>
      </div>

      {/* Six KPI cards in a two-column grid — each card clickable */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        {kpis.map((kpi) => {
          const style = KPI_STYLES[kpi.color] || KPI_STYLES.indigo;
          const Icon = kpi.icon;
          return (
            <Card
              key={kpi.title}
              padding="p-0"
              className="relative overflow-hidden flex items-center gap-3 px-3 sm:px-4 py-3.5 cursor-pointer transition-all duration-200 shadow-md hover:shadow-xl hover:-translate-y-0.5 active:scale-[0.98]"
              onClick={() => navigate(kpi.to)}
            >
              {/* Accent edge + soft color glow */}
              <div className={`absolute inset-y-0 left-0 w-1 bg-gradient-to-b ${style.bar}`} />
              <div className={`absolute -top-6 -right-6 w-16 h-16 rounded-full blur-2xl opacity-20 ${style.glow}`} />
              {isLoadingOrEmpty ? (
                <>
                  <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-gray-100 animate-pulse flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="h-3 w-20 bg-gray-100 rounded animate-pulse mb-2" />
                    <div className="h-6 w-14 bg-gray-100 rounded animate-pulse" />
                  </div>
                </>
              ) : (
                <>
                  <div className={`relative w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md ring-4 bg-gradient-to-br ${style.chip}`}>
                    <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div className="min-w-0 relative">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 leading-snug line-clamp-2" title={kpi.title}>{kpi.title}</p>
                    <p className={`text-lg sm:text-xl font-extrabold tracking-tight ${style.valueText}`}>
                      {Number(kpi.value || 0).toLocaleString()}
                    </p>
                    <p className="text-[10px] sm:text-xs text-gray-400 truncate" title={typeof kpi.subtitle === "string" ? kpi.subtitle : undefined}>{kpi.subtitle}</p>
                  </div>
                </>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default TodayOverviewKPIGrid;